import { createSlice } from "@reduxjs/toolkit";
import { topicApi, coachesByTopicApi } from "./topicApi";

const initialState = {
  topics: [],
  data: [], // alias used by existing components
  success: false,
  message: "",
  isLoading: false,
  error: null,
  categoryId: null, // currently selected category
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    hasMore: false,
  },
  selectedTopic: null, // individual topic data
  topicCoaches: [], // coaches for selected topic
  coachesLoading: false,
  coachesError: null,
  coachesPagination: {
    skip: 0,
    hasMore: false,
  },
};

export const topicSlice = createSlice({
  name: "activeTopics",
  initialState,
  reducers: {
    clearTopicError: (state) => {
      state.error = null;
    },
    resetPagination: (state) => {
      state.pagination = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(topicApi.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
      // Track category intent even while loading
      if (action.meta?.arg?.categoryId) {
        state.categoryId = action.meta.arg.categoryId;
      }
    });
    builder.addCase(topicApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = action.payload.success;
      state.message = action.payload.message;
      state.error = null;

      // Handle individual topic fetching
      if (action.payload.individualTopic) {
        state.selectedTopic = action.payload.data;
      } else {
        // Handle category topics fetching
        state.topics = action.payload.data || [];
        state.data = action.payload.data || [];

        // Handle pagination data
        const pagination = action.payload.pagination;
        if (pagination) {
          const { totalCount, hasMore } = pagination;
          const currentPage = action.meta?.arg?.skip
            ? Math.floor(
                action.meta.arg.skip / (action.meta?.arg?.limit || 10)
              ) + 1
            : 1;
          const pageSize = action.meta?.arg?.limit || 10;

          state.pagination = {
            currentPage,
            pageSize,
            totalItems: totalCount,
            totalPages: Math.ceil(totalCount / pageSize),
            hasMore,
          };
        }

        if (action.meta?.arg?.categoryId) {
          state.categoryId = action.meta.arg.categoryId;
        }
      }
    });
    builder.addCase(topicApi.rejected, (state, action) => {
      state.isLoading = false;
      state.success = false;
      state.topics = [];
      state.data = [];
      state.message = "";
      state.error = action.payload?.message || "Failed to fetch topics";
    });

    // Coaches by topic API handlers
    builder.addCase(coachesByTopicApi.pending, (state) => {
      state.coachesLoading = true;
      state.coachesError = null;
    });
    builder.addCase(coachesByTopicApi.fulfilled, (state, action) => {
      state.coachesLoading = false;
      state.coachesError = null;

      // For infinite scroll, append new coaches if skip > 0, otherwise replace
      if (action.meta?.arg?.skip > 0) {
        state.topicCoaches = [...state.topicCoaches, ...action.payload.data];
      } else {
        state.topicCoaches = action.payload.data || [];
      }

      // Update pagination
      const pagination = action.payload.pagination;
      if (pagination) {
        state.coachesPagination.hasMore = pagination.hasMore;
        state.coachesPagination.skip = action.meta?.arg?.skip || 0;
      }
    });
    builder.addCase(coachesByTopicApi.rejected, (state, action) => {
      state.coachesLoading = false;
      state.topicCoaches = [];
      state.coachesError = action.payload?.message || "Failed to fetch coaches";
    });
  },
});

export const { clearTopicError, resetPagination, setPagination } =
  topicSlice.actions;
export default topicSlice.reducer;
